import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Alert, App, Button, Modal } from 'antd'
import type { BillSearchBatchInput } from '@super-mega/server/contracts/bill'
import { useBatchJobCompletion } from '@/hooks/use-batch-job-completion.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getBatchJobStatusLabel } from '@/utils/batch-job.ts'
import { BillBatchResult } from './bill-batch-result.tsx'
import { BILL_ACTION_ITEMS } from './const/bill-actions.ts'

export type BillSearchBatchTaskInfo = {
  jobId: string
  count: number
  action: BillSearchBatchInput['action']
}

/** 由列表持有任务，关闭进度弹窗不卸载查询，也不丢失结果入口。 */
export function BillSearchBatchTask({
  task,
  onSaved
}: {
  task: BillSearchBatchTaskInfo
  onSaved: () => Promise<void>
}) {
  const trpc = useTRPC()
  const { message } = App.useApp()
  const [open, setOpen] = useState(true)
  const status = useQuery({
    ...trpc.bill.batchBySearchStatus.queryOptions({ jobId: task.jobId }),
    refetchInterval: (query) =>
      ['completed', 'failed', 'missing'].includes(query.state.data?.state ?? '') ? false : 3000
  })
  const state = status.data?.state
  useBatchJobCompletion({
    jobId: task.jobId,
    state,
    onComplete: onSaved,
    onError: () => {
      void message.warning('列表刷新失败，请手动刷新查看结果')
    }
  })
  const title = BILL_ACTION_ITEMS.find((item) => item.key === task.action)!.label
  return (
    <>
      <Button onClick={() => setOpen(true)}>
        查看批量任务：{title}（{task.count} 张，{getBatchJobStatusLabel(state)}）
      </Button>
      <Modal
        open={open}
        title={`按搜索${title}（${task.count} 张）`}
        width="40rem"
        onCancel={() => setOpen(false)}
        footer={<Button onClick={() => setOpen(false)}>关闭</Button>}
      >
        <div className="flex flex-col gap-4">
          <Alert
            showIcon
            type={state === 'failed' ? 'error' : 'info'}
            title={`任务${getBatchJobStatusLabel(state)}：${status.data?.processed ?? 0}/${task.count}`}
            description={
              <>
                任务 ID：{task.jobId}
                <p>关闭窗口后任务会继续执行，可通过「查看批量任务」重新打开。</p>
                {status.data?.message && <p>{status.data.message}</p>}
              </>
            }
          />
          {status.isError && (
            <Alert
              type="error"
              title={status.error.message}
              action={<Button onClick={() => void status.refetch()}>重试查询</Button>}
            />
          )}
          {Boolean(status.data?.results.length) && <BillBatchResult results={status.data!.results} />}
        </div>
      </Modal>
    </>
  )
}
